<script>
import _ from 'lodash'

export default {
  inheritAttrs: false,
  props: {
    label: {
      type: String,
      required: true,
    },
    subLabel: {
      type: String,
      default: '',
    },
    checkboxSubLabels: {
      type: Object,
      default: () => ({}),
    },
    value: {
      type: [String, Number, Object, Array],
      default: '',
    },
    valueArray: {
      type: Array,
      default: () => [],
    },
    editMode: {
      type: Boolean,
    },
    immediateValidation: {
      type: Boolean,
    },
    allowThirdParty: {
      type: Boolean,
    },
    disabled: {
      type: Boolean,
    },
    editCheckboxValues: {
      type: [Object, Array],
      default: null,
    },
    disabledCheckboxValues: {
      type: Array,
      default: () => [],
    },
    exclusiveValues: {
      type: Array,
      default: () => [],
    },
    error: {
      type: String,
      default: '',
    },
    rules: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      modelValue: this.value,
      oldValue: this.value,
      originalModelValue: this.value,
    }
  },
  computed: {
    isRequired() {
      return this.rules.includes('required')
    },
  },
  watch: {
    value: {
      handler: function (newVal) {
        this.modelValue = newVal
        this.oldValue = newVal
      },
    },
  },
  methods: {
    onInput(inputEvent) {
      let value = inputEvent
      if (inputEvent instanceof Event) {
        value = inputEvent.target?.value || null
      }

      const newlyInsertedValue = _.head(_.difference(value, this.oldValue))
      if (newlyInsertedValue) {
        this.exclusiveValues.forEach((list) => {
          if (list.includes(newlyInsertedValue)) {
            const valuesToRemove = _.difference(list, [newlyInsertedValue])
            value = value.filter((item) => !valuesToRemove.includes(item))
          }
        })
      }

      this.oldValue = value
      this.$refs.checkboxValidationProvider.setFlags({
        changed: !_.isEqual(_.sortBy(this.oldValue), _.sortBy(this.originalModelValue)),
      })
      this.$emit('update:value', value)
    },
  },
}
</script>

<template>
  <div :class="$style.container">
    <slot name="label">
      <p :class="$style.label" class="p1">
        {{ label }}:
        <span v-if="isRequired" data-required-asterisk :class="$style.requiredAsterisk">*</span>
      </p>
    </slot>
    <p v-if="subLabel && editMode" :class="$style.formItemSubLabel">{{ subLabel }}</p>

    <template v-if="!editMode">
      <ul v-if="value.length" :class="$style.valuesList">
        <li v-for="item in value" :key="item">
          {{ item }}
        </li>
      </ul>
      <h4 v-else :class="$style.value">{{ 'N/A' }}</h4>
    </template>
    <template v-if="editMode">
      <ValidationProvider
        v-slot="{ errors }"
        ref="checkboxValidationProvider"
        :immediate="immediateValidation"
        :debounce="300"
        :name="label"
        :rules="rules">
        <div v-for="checkboxValue in editCheckboxValues" :key="checkboxValue" :class="$style.checkboxContainer">
          <BaseCheckbox
            v-model="modelValue"
            :class="$style.checkboxItem"
            :label="checkboxValue"
            :input-value="checkboxValue"
            :disabled="disabled || disabledCheckboxValues.includes(checkboxValue)"
            @input="onInput" />
          <p
            v-if="checkboxSubLabels[checkboxValue] && editMode"
            :class="$style.formItemCheckboxSubLabel"
            v-html="checkboxSubLabels[checkboxValue]" />
        </div>
        <p v-if="errors[0]" :class="$style.errorMessage" class="tag-text">{{ errors[0] }}</p>
      </ValidationProvider>
    </template>
  </div>
</template>

<style lang="scss" module>
@import '@design';

.container {
  overflow-y: initial;

  div[class*='errorContainer'] {
    position: relative;
  }
}

.checkboxContainer {
  @media (max-width: $lg-breakpoint) {
    flex-direction: column;
  }

  display: flex;
  margin-bottom: 10px;
}

.checkboxItem {
  @media (max-width: $lg-breakpoint) {
    margin-bottom: 5px;
  }

  margin-bottom: 10px;

  p {
    width: 160px;
  }
}

.label {
  margin-top: 10px;
  margin-bottom: 10px;
  color: $dark-gray;
}

.value {
  font-weight: 700;
  color: $primary-blue;
  overflow-wrap: break-word;
}

.valuesList {
  padding-left: 0;
  font-weight: 700;
  list-style: none;

  li {
    margin-bottom: 5px;
    font-size: 16px;
    font-weight: 700;
    color: $primary-blue;
  }
}

.requiredAsterisk {
  font-weight: 700;
  color: $secondary-red;
}

.formItemSubLabel {
  margin: -8px 0 6px;
  color: $secondary-light-blue;
}

.formItemCheckboxSubLabel {
  @media (max-width: $lg-breakpoint) {
    margin-left: 30px;
  }

  margin-left: 10px;
  color: $secondary-light-blue;

  span {
    display: block;
    height: 10px;
  }
}

.errorMessage {
  @extend %error-message;

  text-align: right;
}
</style>
